import { computed, inject, onMounted, onUnmounted, ref, toValue, type MaybeRefOrGetter } from 'vue';

import { efFormSymbol } from '@lib/components/form/efFormSymbol';

/**
 * The validation half of a form field, shared by every ef input: the named rules, the verdict
 * the field reports to a surrounding efForm, and the part of the verdict it shows the user.
 *
 * A rule is a named predicate over the value. The name is the key its message is looked up
 * under (`validations.<name>` in the locale), so a form states what is wrong in the field
 * rather than after a rejected save.
 *
 * What is shown and what is reported are not the same thing. A field that was never touched
 * keeps quiet about being empty - the star on its label already says it is needed, and a
 * dialog that opens shouting at every field it has not been given yet is noise, until the
 * form is asked to reveal everything (efForm's `reveal`, on a refused submit). Anything
 * else, a value that is there and wrong, is shown at once. The verdict itself is strict
 * either way, so the form's ok button is never enabled over an unfinished field - unless the
 * form says empty is fine (efForm's `allowEmpty`), then only an empty required field passes.
 */

export type Validator<T> = (item: T) => boolean;

export type ValidationOptions<T> = {
	/** The field's uid, the key it is registered under. */
	uid: string;
	/** The value to judge, usually the field's model. */
	value: MaybeRefOrGetter<T | null | undefined>;
	/** The named rules of the field, as its `validations` prop carries them. */
	validations?: MaybeRefOrGetter<Record<string, Validator<T>> | undefined>;
	/** Adds the `required` rule, and with it the star on the label. */
	required?: MaybeRefOrGetter<boolean | undefined>;
	/** What counts as empty for `required`; an absent value or blank text by default. */
	isEmpty?: (value: T) => boolean;
	/** ANDed into the verdict, for a field that is judged from outside as well. */
	valid?: MaybeRefOrGetter<boolean | undefined>;
};

function isBlank(value: unknown): boolean {
	return value === null || value === undefined || String(value).trim() === '';
}

export default function useValidations<T>(options: ValidationOptions<T>) {
	const form = inject(efFormSymbol, undefined);

	/** Set once the user has been in the field, which is when an empty one may be pointed out. */
	const touched = ref(false);

	const current = computed<T>(() => (toValue(options.value) ?? null) as T);

	const activeValidations = computed<Record<string, Validator<T>>>(() => {
		const rules: Record<string, Validator<T>> = { ...(toValue(options.validations) ?? {}) };

		if (toValue(options.required)) {
			rules['required'] = (item) => !(options.isEmpty ?? isBlank)(item);
		}

		return rules;
	});

	// Starred is a field that may not be left empty; a rule that only bounds what is entered is not one.
	const isRequired = computed<boolean>(() => 'required' in activeValidations.value);

	/** Whether the label carries the star: required, unless the form leaves the stars off. */
	const showsRequiredMark = computed<boolean>(() => isRequired.value && !form?.hidesRequiredMarks());

	const failedValidations = computed<string[]>(() =>
		Object.entries(activeValidations.value)
			.filter(([, validator]) => !validator(current.value))
			.map(([key]) => key),
	);

	/** The verdict: strict, and what a surrounding efForm collects. */
	const isValid = computed<boolean>(
		() =>
			(toValue(options.valid) ?? true) &&
			failedValidations.value.filter((key) => key !== 'required' || !form?.allowsEmpty()).length === 0,
	);

	/** What the field writes underneath itself - the verdict minus what it is too early to say. */
	const visibleValidations = computed<string[]>(() =>
		touched.value || form?.revealed() ?
			failedValidations.value
		:	failedValidations.value.filter((key) => key !== 'required'),
	);

	/** What the field paints red, which follows the messages it shows. */
	const isVisiblyValid = computed<boolean>(
		() => (toValue(options.valid) ?? true) && visibleValidations.value.length === 0,
	);

	/** Called by the field once the user has been in it: on blur, or on picking a value. */
	function touch(): void {
		touched.value = true;
	}

	onMounted(() => form?.register(options.uid, isValid));
	onUnmounted(() => form?.unregister(options.uid));

	return {
		touched,
		touch,
		isRequired,
		showsRequiredMark,
		failedValidations,
		visibleValidations,
		isValid,
		isVisiblyValid,
	};
}
